import { type } from 'arktype';
import { joinStatusSchema } from '../experienceJoins';
import { experienceAccessLabelSchema, experienceStatusSchema } from '../experiences';
import { createPaginatedResponseSchema } from '../shared/pagination';
import { ageRangeSchema, archetypeSchema, userTierSchema } from '../users';

/**
 * Las siete fases del ciclo de vida de una experiencia oficial en el panel del centro de
 * mando (documento 06a §5.1): se derivan de `status` más los dos sellos (`reviewedAt` y
 * `confirmedAt`) antes de publicar, nunca se guardan como columna propia.
 */
export const EXPERIENCE_PHASES = [
	'propuestaRecibida',
	'enPreparacion',
	'copyListo',
	'publicada',
	'enCurso',
	'terminadaPendienteDeCerrar',
	'archivada',
] as const;
export const experiencePhaseSchema = type.enumerated(...EXPERIENCE_PHASES);
export type ExperiencePhase = typeof experiencePhaseSchema.infer;

/**
 * Las seis tareas rápidas del panel (documento 06a §5.1 y CA-06a.14): lo que le queda por
 * hacer a quien modera para cada experiencia, derivado de su fase y de sus contadores.
 */
export const QUICK_TASKS = ['revisar', 'confirmar', 'publicar', 'atenderSolicitudes', 'cerrar', 'faltaGente'] as const;
export const quickTaskSchema = type.enumerated(...QUICK_TASKS);
export type QuickTask = typeof quickTaskSchema.infer;

/** Los cinco niveles de asistencia (espejo de `NivelAsistencia` de `rules/joins.ts`, documento 05a §6.6). */
export const ATTENDANCE_LEVELS = ['alta', 'media', 'baja', 'advertenciaFinal', 'cuentaBloqueada'] as const;
export const attendanceLevelSchema = type.enumerated(...ATTENDANCE_LEVELS);
export type AttendanceLevel = typeof attendanceLevelSchema.infer;

/**
 * Una fila del panel del centro de mando (documento 06b §10.1): `GET /experience-moderation`.
 * `reviewed`/`confirmed` son los dos sellos ya puestos o no (documento 06a §5.1); `capacity`,
 * `seatsLeft`, `startDate` y `endDate` nullable con el mismo criterio que el resto de DTOs de
 * "experience" (`experienceResponseSchema`).
 */
export const moderationBoardItemResponseSchema = type({
	accessLabel: experienceAccessLabelSchema,
	'capacity?': 'number.integer | null',
	confirmed: 'boolean',
	confirmedCount: 'number.integer',
	'endDate?': 'string.date.iso | null',
	id: 'string.uuid',
	'oldestRequestBusinessHours?': 'number | null',
	pendingRequests: 'number.integer',
	phase: experiencePhaseSchema,
	quickTasks: quickTaskSchema.array(),
	reviewed: 'boolean',
	'seatsLeft?': 'number.integer | null',
	'startDate?': 'string.date.iso | null',
	status: experienceStatusSchema,
	title: 'string',
	viewerIsOrganizer: 'boolean',
}).onUndeclaredKey('delete');

export type ModerationBoardItemResponse = typeof moderationBoardItemResponseSchema.infer;

export const moderationBoardPaginatedResponseSchema = createPaginatedResponseSchema(moderationBoardItemResponseSchema);
export type ModerationBoardPaginatedResponse = typeof moderationBoardPaginatedResponseSchema.infer;

/**
 * Una línea de la ficha de solicitudes de una experiencia oficial (documento 06b §10.4,
 * CA-06b.4): `GET /experiences/:id/moderation-requests`. `id` es el de la fila
 * "experienceJoin", no el de la persona; `userId` la identifica.
 */
export const moderationRequestResponseSchema = type({
	absences: 'number.integer',
	'ageRange?': ageRangeSchema,
	'archetype?': archetypeSchema,
	attendanceLevel: attendanceLevelSchema,
	attendanceRatioPct: 'number',
	'avatarUrl?': 'string',
	blocked: 'boolean',
	'blockedUntil?': 'string.date.iso',
	'fullName?': 'string',
	id: 'string.uuid',
	'instagram?': 'string',
	requestedAt: 'string.date.iso',
	status: joinStatusSchema,
	tier: userTierSchema,
	userId: 'string.uuid',
	'username?': 'string',
	waitingBusinessHours: 'number',
	'whatsapp?': 'string',
}).onUndeclaredKey('delete');

export type ModerationRequestResponse = typeof moderationRequestResponseSchema.infer;

export const moderationRequestsResponseSchema = type({ items: moderationRequestResponseSchema.array() });
export type ModerationRequestsResponse = typeof moderationRequestsResponseSchema.infer;

/**
 * Una persona en la pantalla de cierre (documento 06b §10.2): `GET /experiences/:id/attendance`.
 * `id` es el de la fila "experienceJoin"; `previousAbsences` son los `NO_SHOW` acumulados ANTES
 * de este cierre (CA-06b.9 — el cierre no cuenta la ausencia que él mismo está a punto de
 * registrar). `warned` marca a quien canceló con `MARGEN_AVISO_HORAS` o más de antelación: sale
 * en su propio grupo y no es marcable como ausente.
 */
export const moderationAttendeeResponseSchema = type({
	'avatarUrl?': 'string',
	'fullName?': 'string',
	id: 'string.uuid',
	previousAbsences: 'number.integer',
	status: joinStatusSchema,
	userId: 'string.uuid',
	warned: 'boolean',
}).onUndeclaredKey('delete');

export type ModerationAttendeeResponse = typeof moderationAttendeeResponseSchema.infer;

export const moderationAttendanceResponseSchema = type({
	confirmed: moderationAttendeeResponseSchema.array(),
	warned: moderationAttendeeResponseSchema.array(),
});

export type ModerationAttendanceResponse = typeof moderationAttendanceResponseSchema.infer;

/**
 * `PATCH /experiences/:id/moderation-review` (documento 06a §6): pone o quita cualquiera de
 * los dos sellos. Ambos opcionales porque se puede tocar uno solo por petición.
 */
export const reviewExperienceSchema = type({
	'confirmed?': 'boolean',
	'reviewed?': 'boolean',
}).onUndeclaredKey('delete');

export type ReviewExperience = typeof reviewExperienceSchema.infer;

/**
 * `POST /experiences/:id/close` (CL-06a.5): cierra la experiencia con la lista de AUSENTES
 * más una confirmación explícita obligatoria — `acknowledged` es un literal `true`, no un
 * booleano cualquiera: sin él, la petición ni siquiera es válida.
 */
export const closeExperienceSchema = type({
	absentUserIds: 'string.uuid[]',
	acknowledged: 'true',
}).onUndeclaredKey('delete');

export type CloseExperience = typeof closeExperienceSchema.infer;

/** La respuesta de `POST /experiences/:id/close`: el resumen de lo que acaba de pasar. */
export const closeExperienceResponseSchema = type({
	absentCount: 'number.integer',
	attendedCount: 'number.integer',
	blockedUserIds: 'string.uuid[]',
	experienceId: 'string.uuid',
}).onUndeclaredKey('delete');

export type CloseExperienceResponse = typeof closeExperienceResponseSchema.infer;
